// City > Resources subtab. Inventory + per-minute prod/cons/net for
// every active resource, grouped by category. Each row is clickable
// to expand a "Where it's going" drilldown showing every producer,
// consumer, service input, citizen drain, and NPC trade flow for that
// resource.
//
// Per-partner trade activity table is a follow-up (commit 51) so this
// commit stays focused on the flow breakdown.
import { state } from '../../state/store.js';
import { sb, fetchAllPaged } from '../../api/supabase.js';
import { computeResourceProdCons, computeResourceFlow } from '../../scenes/helpers.js';
import { escapeHtml } from '../util.js';

const GROUPS = [
  { label: 'Raw materials',     match: (r) => r.kind === 'raw' && !r.is_food },
  { label: 'Raw food',          match: (r) => r.kind === 'raw' && r.is_food },
  { label: 'Processed',         match: (r) => r.kind === 'processed' && !r.is_food && !r.is_luxury_food && !r.is_industrial_luxury },
  { label: 'Cooked food',       match: (r) => r.kind === 'processed' && r.is_food && !r.is_luxury_food },
  { label: 'Industrial luxury', match: (r) => r.is_industrial_luxury },
  { label: 'Luxury food',       match: (r) => r.is_luxury_food }
];

// Module-scope expansion state — persists across panel refreshes so
// the drilldown stays open while the tick loop re-renders.
const expanded = new Set();

// Category collapse state — persisted to localStorage so the user's
// preference survives reloads. Default: every category open.
const COLLAPSE_KEY = 'city_resources_collapsed_v2';
const collapsed = new Set(loadCollapsed());
function loadCollapsed() {
  try {
    const raw = localStorage.getItem(COLLAPSE_KEY);
    if (!raw) return [];
    return JSON.parse(raw) || [];
  } catch (_e) { return []; }
}
function persistCollapsed() {
  try { localStorage.setItem(COLLAPSE_KEY, JSON.stringify([...collapsed])); } catch (_e) { /* no-op */ }
}

// Search filter — prefix-match on resource name. Cleared between
// mounts to avoid stale state; not persisted, since this is an
// in-the-moment "find pottery" affordance.
let filterText = '';

// Cached per-partner trade flows for the trailing 7 days. Async-loaded
// on first render; re-fetched after CACHE_MS. The drilldown renders
// the per-partner table from `cachedFlows.byPartner` when available,
// silently skipping it otherwise (so the flow-breakdown stays useful
// even before trade data arrives).
let cachedFlows = null;
let cacheFetchedAt = 0;
let pendingFlowsFetch = null;
const CACHE_MS = 60 * 1000;

function loadTradeFlows(parent) {
  const now = Date.now();
  if (cachedFlows && now - cacheFetchedAt < CACHE_MS) return;
  if (pendingFlowsFetch) return;
  const uid = state.currentUser?.id;
  if (!uid) return;
  const since = new Date(now - 7 * 24 * 60 * 60 * 1000).toISOString();
  pendingFlowsFetch = (async () => {
    try {
      const [tx, offers] = await Promise.all([
        fetchAllPaged(() => sb.from('trade_transactions')
          .select('*').eq('player_id', uid).gte('created_at', since)
          .order('created_at', { ascending: false })),
        fetchAllPaged(() => sb.from('player_trade_offers')
          .select('*').eq('status', 'accepted').gte('resolved_at', since)
          .or(`from_player_id.eq.${uid},to_player_id.eq.${uid}`)
          .order('resolved_at', { ascending: false }))
      ]);
      const ids = new Set();
      for (const o of offers) {
        if (o.from_player_id && o.from_player_id !== uid) ids.add(o.from_player_id);
        if (o.to_player_id && o.to_player_id !== uid) ids.add(o.to_player_id);
      }
      let nameMap = {};
      if (ids.size > 0) {
        const { data } = await sb.from('player_profiles')
          .select('id, display_name').in('id', Array.from(ids));
        for (const p of (data || [])) nameMap[p.id] = p.display_name;
      }
      cachedFlows = aggregateTradeFlows(uid, tx, offers, nameMap);
      cacheFetchedAt = Date.now();
      // Re-render so the drilldown picks up the new data.
      if (parent && parent.isConnected) renderCityResources(parent);
    } catch (_e) {
      // Trade flow fetch failed — leave cachedFlows alone so the
      // drilldown stays usable; just no per-partner table.
    } finally {
      pendingFlowsFetch = null;
    }
  })();
}

// Roll up trade_transactions (NPC) + accepted player_trade_offers
// (P2P) into per-resource per-partner buckets. Each partner entry
// carries export_qty/export_money + import_qty/import_money so the
// table can show "you sent N (+$M)" / "you got N (-$M)" side by side.
//
// Also rolls a separate per-resource bucket scoped to today (UTC
// midnight → now) so the row table can show today's net $ per
// resource. Aligned with Treasury (which buckets cash_transactions by
// UTC day server-side) so the two displays read the same boundary —
// previously this used local midnight, which on US-Eastern (UTC-4)
// would show 4 extra hours of "today" that Treasury didn't.
function aggregateTradeFlows(uid, transactions, offers, nameMap) {
  const byPartner = {};
  const byResourceToday = {};
  const todayStart = (() => {
    const now = new Date();
    return Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  })();

  const bump = (rk, partnerKey, name, kind, playerId, dir, qty, money) => {
    if (!byPartner[rk]) byPartner[rk] = [];
    let entry = byPartner[rk].find((p) => p.partnerKey === partnerKey);
    if (!entry) {
      entry = { partnerKey, name, kind, player_id: playerId,
        export_qty: 0, export_money: 0, import_qty: 0, import_money: 0 };
      byPartner[rk].push(entry);
    }
    if (dir === 'export') { entry.export_qty += qty; entry.export_money += money; }
    else                  { entry.import_qty += qty; entry.import_money += money; }
  };

  const bumpToday = (rk, dir, money) => {
    if (!byResourceToday[rk]) byResourceToday[rk] = { import_money: 0, export_money: 0 };
    if (dir === 'export') byResourceToday[rk].export_money += money;
    else                  byResourceToday[rk].import_money += money;
  };

  for (const t of transactions) {
    const dir = t.transaction_type === 'sell' ? 'export' : 'import';
    const traderName = state.traders?.[t.trader_key]?.name || t.trader_key;
    const money = Number(t.total_price || 0);
    bump(t.resource_key, t.trader_key, traderName, 'npc', null, dir,
      Number(t.quantity || 0), money);
    if (t.created_at && new Date(t.created_at).getTime() >= todayStart) {
      bumpToday(t.resource_key, dir, money);
    }
  }

  for (const o of offers) {
    const iAmSender = o.from_player_id === uid;
    const otherId = iAmSender ? o.to_player_id : o.from_player_id;
    const partnerKey = 'player:' + otherId;
    const partnerName = nameMap[otherId] || 'Player';
    // give_resources / receive_resources are canonically OBJECTS
    // ({clay: 5}) — that's what server propose_trade stores via
    // jsonb_each_text. An earlier "fix" treated them as arrays of
    // {resource_key, quantity}; that was wrong for the actual stored
    // shape and silently dropped every P2P trade row even after the
    // explicit attempt to surface them. Defensively accept either
    // shape so any stale rows still render.
    const normalize = (r) => {
      if (!r) return [];
      if (Array.isArray(r)) {
        return r.filter((e) => e?.resource_key).map((e) => [e.resource_key, Number(e.quantity || 0)]);
      }
      if (typeof r === 'object') {
        return Object.entries(r).map(([k, v]) => [k, Number(v)]);
      }
      return [];
    };
    const giveRes = normalize(o.give_resources);
    const recvRes = normalize(o.receive_resources);
    const myExports = iAmSender ? giveRes : recvRes;
    const myImports = iAmSender ? recvRes : giveRes;
    for (const [rk, qty] of myExports) {
      if (qty > 0) bump(rk, partnerKey, partnerName, 'player', otherId, 'export', qty, 0);
    }
    for (const [rk, qty] of myImports) {
      if (qty > 0) bump(rk, partnerKey, partnerName, 'player', otherId, 'import', qty, 0);
    }
  }

  return { byPartner, byResourceToday };
}

export function renderCityResources(parent) {
  // Kick off the trade-flow fetch in the background — first render
  // misses it, second render after fetch lands picks it up.
  loadTradeFlows(parent);

  const { prod, cons } = computeResourceProdCons(
    state.allBuildings, state.buildingTypes, state.currentUser?.id, state.profile
  );

  const all = Object.values(state.resourceNodes).filter((r) => r.is_active);
  const lowFilter = filterText.trim().toLowerCase();
  let html = `
    <div class="cr-filter-row">
      <input type="search" class="cr-filter" placeholder="Filter resources…" value="${escapeHtml(filterText)}" />
    </div>
  `;
  for (const group of GROUPS) {
    let rows = all.filter(group.match).sort((a, b) => a.name.localeCompare(b.name));
    if (lowFilter) rows = rows.filter((r) => r.name.toLowerCase().includes(lowFilter));
    if (!rows.length) continue;
    const isCollapsed = collapsed.has(group.label);
    const totalStock = rows.reduce((s, r) => s + Math.floor(Number(state.inventory?.[r.key] || 0)), 0);
    html += `<div class="cr-section ${isCollapsed ? 'cr-collapsed' : ''}" data-group="${escapeHtml(group.label)}">
      <h3 class="cr-section-title cr-section-toggle">
        <span class="cr-section-chev">${isCollapsed ? '▸' : '▾'}</span>
        ${escapeHtml(group.label)}
        <small class="cr-section-summary">${rows.length} · ${totalStock.toLocaleString()} total</small>
      </h3>
      ${isCollapsed ? '' : `<div class="cr-table">
        <div class="cr-row cr-row-head">
          <span class="cr-cell cr-cell-name">Resource</span>
          <span class="cr-cell cr-cell-inv">Inv</span>
          <span class="cr-cell cr-num">Prod</span>
          <span class="cr-cell cr-num">Cons</span>
          <span class="cr-cell cr-num">Net</span>
          <span class="cr-cell cr-num" title="Net $ flow on this resource since UTC midnight (matches Treasury)">$ Today</span>
        </div>
        ${rows.map((r) => renderRow(r, prod, cons)).join('')}
      </div>`}
    </div>`;
  }
  parent.innerHTML = html || '<p class="cr-empty">No active resources.</p>';

  // Filter input
  const filterEl = parent.querySelector('.cr-filter');
  if (filterEl) {
    filterEl.addEventListener('input', (e) => {
      filterText = e.target.value || '';
      renderCityResources(parent);
      // Re-focus the input + restore caret position after re-render.
      const fresh = parent.querySelector('.cr-filter');
      if (fresh) {
        fresh.focus();
        fresh.setSelectionRange(filterText.length, filterText.length);
      }
    });
  }

  // Category collapse toggles
  parent.querySelectorAll('.cr-section-toggle').forEach((header) => {
    header.addEventListener('click', () => {
      const label = header.parentElement.dataset.group;
      if (collapsed.has(label)) collapsed.delete(label);
      else collapsed.add(label);
      persistCollapsed();
      renderCityResources(parent);
    });
  });

  parent.querySelectorAll('.cr-row-data[data-resource]').forEach((row) => {
    row.addEventListener('click', () => {
      const rk = row.dataset.resource;
      if (expanded.has(rk)) expanded.delete(rk);
      else expanded.add(rk);
      renderCityResources(parent);
    });
  });

  // Trade-policy controls inside the drilldown. Every click/change/
  // input inside the policy block stops propagation so it doesn't
  // toggle the parent row's expand/collapse.
  parent.querySelectorAll('.cr-detail .cr-policy').forEach((box) => {
    box.addEventListener('click', (e) => e.stopPropagation());
  });

  // Mode-change reveals the matching price field (sell vs buy).
  parent.querySelectorAll('.cr-policy-mode').forEach((sel) => {
    sel.addEventListener('change', () => {
      const box = sel.closest('.cr-policy');
      if (!box) return;
      const mode = sel.value;
      box.querySelector('.cr-policy-sell').style.display = (mode === 'sell_surplus') ? '' : 'none';
      box.querySelector('.cr-policy-buy').style.display  = (mode === 'buy_to_reserve') ? '' : 'none';
      const reserve = box.querySelector('.cr-policy-reserve');
      if (reserve) reserve.disabled = (mode === 'hold');
    });
  });

  parent.querySelectorAll('.cr-policy-save').forEach((btn) => {
    btn.addEventListener('click', async (e) => {
      e.stopPropagation();
      const rk = btn.dataset.resource;
      const box = btn.closest('.cr-policy');
      if (!box) return;
      const mode = box.querySelector('.cr-policy-mode').value;
      const reserve = parseInt(box.querySelector('.cr-policy-reserve').value, 10) || 0;
      const msRaw = box.querySelector('.cr-policy-minsell')?.value;
      const mbRaw = box.querySelector('.cr-policy-maxbuy')?.value;
      const minSell = msRaw === '' || msRaw == null ? null : parseInt(msRaw, 10);
      const maxBuy  = mbRaw === '' || mbRaw == null ? null : parseInt(mbRaw, 10);
      btn.disabled = true; btn.textContent = 'Saving…';
      try {
        const { error } = await sb.rpc('save_trade_policy', {
          p_resource_key: rk, p_mode: mode,
          p_reserve_target: reserve, p_min_sell_price: minSell, p_max_buy_price: maxBuy
        });
        if (error) throw error;
        state.tradePolicies[rk] = { mode, reserve_target: reserve, min_sell_price: minSell, max_buy_price: maxBuy };
        btn.textContent = 'Saved';
        setTimeout(() => { btn.disabled = false; btn.textContent = 'Save'; }, 1200);
      } catch (err) {
        alert('Policy save failed: ' + (err.message || err));
        btn.disabled = false; btn.textContent = 'Save';
      }
    });
  });
}

function renderRow(r, prod, cons) {
  const inv = Math.floor(Number(state.inventory?.[r.key] || 0));
  const p = Number(prod[r.key] || 0);
  const c = Number(cons[r.key] || 0);
  const net = p - c;
  const netClass = net > 0.05 ? 'cr-pos' : net < -0.05 ? 'cr-neg' : '';
  const isOpen = expanded.has(r.key);
  const chev = isOpen ? '▾' : '▸';

  const today = cachedFlows?.byResourceToday?.[r.key];
  const todayNet = today ? Math.round((today.export_money || 0) - (today.import_money || 0)) : null;
  const todayClass = todayNet > 0 ? 'cr-pos' : todayNet < 0 ? 'cr-neg' : '';
  const todayCell = todayNet === null || todayNet === 0
    ? '—'
    : (todayNet > 0 ? '+$' : '−$') + Math.abs(todayNet).toLocaleString();

  return `<div class="cr-row cr-row-data ${isOpen ? 'cr-open' : ''}" data-resource="${escapeHtml(r.key)}">
    <span class="cr-cell cr-cell-name"><span class="cr-chev">${chev}</span>${escapeHtml(r.name)}</span>
    <span class="cr-cell cr-cell-inv">${inv.toLocaleString()}</span>
    <span class="cr-cell cr-num cr-pos">${p > 0 ? '+' + p.toFixed(1) : '—'}</span>
    <span class="cr-cell cr-num cr-neg">${c > 0 ? '−' + c.toFixed(1) : '—'}</span>
    <span class="cr-cell cr-num ${netClass}">${net !== 0 ? (net > 0 ? '+' : '') + net.toFixed(1) : '—'}</span>
    <span class="cr-cell cr-num ${todayClass}">${todayCell}</span>
  </div>
  ${isOpen ? `<div class="cr-detail">${renderFlowHtml(r.key)}</div>` : ''}`;
}

function renderFlowHtml(resourceKey) {
  const ctx = {
    allBuildings: state.allBuildings,
    buildingTypes: state.buildingTypes,
    resources: state.resourceNodes,
    housingTierConfig: state.housingTierConfig,
    housingLifestyleDemands: state.housingLifestyleDemands,
    inventory: state.inventory,
    tradePolicies: state.tradePolicies,
    traders: state.traders,
    allTraderPrices: state.allTraderPrices,
    profile: state.profile
  };
  const flow = computeResourceFlow(resourceKey, ctx, state.currentUser?.id);

  let totalIn = 0;
  for (const p of flow.production) totalIn += p.rate;
  for (const i of flow.imports)    totalIn += i.rate;
  let totalOut = flow.citizens || 0;
  for (const p of flow.processing) totalOut += p.rate;
  for (const s of flow.services)   totalOut += s.rate;
  for (const e of flow.exports)    totalOut += e.rate;

  // Empty-flow note is informational, not a short-circuit — the
  // player still needs the policy editor + partner table below so
  // they can set up imports for resources they don't yet produce
  // ("can't make iron yet, but I want to buy-to-reserve 20").
  let html = '';
  if (totalIn === 0 && totalOut === 0) {
    html += `<div class="cr-flow-empty">No production or consumption right now.</div>`;
  }
  if (flow.production.length > 0 || flow.imports.length > 0) {
    html += `<div class="cr-flow-section">
      <div class="cr-flow-section-title cr-pos">Producing +${(Math.round(totalIn * 10) / 10).toFixed(1)}/min</div>`;
    for (const p of flow.production) {
      html += `<div class="cr-flow-row">
        <span>${p.count}× ${escapeHtml(p.name)}</span>
        <span class="cr-pos">${fmtRate(p.rate)}</span>
      </div>`;
    }
    for (const i of flow.imports) {
      html += `<div class="cr-flow-row">
        <span>${escapeHtml(i.trader)} <small>(buy-to-reserve @ $${i.price})</small></span>
        <span class="cr-pos">${fmtRate(i.rate)} max</span>
      </div>`;
    }
    html += `</div>`;
  }

  if (totalOut > 0) {
    html += `<div class="cr-flow-section">
      <div class="cr-flow-section-title cr-neg">Consuming −${(Math.round(totalOut * 10) / 10).toFixed(1)}/min</div>`;
    for (const p of flow.processing) {
      const out = p.output ? ' → ' + escapeHtml(p.output) : '';
      html += `<div class="cr-flow-row">
        <span>${p.count}× ${escapeHtml(p.name)}${out}</span>
        <span class="cr-neg">−${(Math.round(p.rate * 10) / 10).toFixed(1)}/min</span>
      </div>`;
    }
    for (const s of flow.services) {
      html += `<div class="cr-flow-row">
        <span>${s.count}× ${escapeHtml(s.name)} <small>(service input)</small></span>
        <span class="cr-neg">−${(Math.round(s.rate * 10) / 10).toFixed(1)}/min</span>
      </div>`;
    }
    if (flow.citizens > 0) {
      html += `<div class="cr-flow-row">
        <span>Citizens <small>(eaten by housing)</small></span>
        <span class="cr-neg">−${(Math.round(flow.citizens * 10) / 10).toFixed(1)}/min</span>
      </div>`;
    }
    for (const e of flow.exports) {
      html += `<div class="cr-flow-row">
        <span>${escapeHtml(e.trader)} <small>(sell-surplus @ $${e.price})</small></span>
        <span class="cr-neg">−${(Math.round(e.rate * 10) / 10).toFixed(1)}/min max</span>
      </div>`;
    }
    html += `</div>`;
  }

  const net = totalIn - totalOut;
  const netClass = net > 0.05 ? 'cr-pos' : net < -0.05 ? 'cr-neg' : '';
  html += `<div class="cr-flow-net">Net: <span class="${netClass}">${fmtRate(net)}</span></div>`;

  // Trade policy editor — same fields as Trade > Partners, but
  // scoped to this one resource. Lets the player set sell/buy gates
  // without leaving the drilldown they were already reading.
  html += renderTradePolicy(resourceKey);

  // Per-partner trade activity (last 7 days). Renders only when the
  // flow data has been fetched; silently skipped pre-load.
  html += renderPartnerTable(resourceKey);
  return html;
}

// NPC trade policy editor for one resource. Mirrors v1's
// reports.js renderResourceDrilldownHtml policy block + v2's
// existing TradePartnersTab.renderResourceRow controls. Same RPC
// call (save_trade_policy) so policies edited here, in the Partners
// tab, or by the auto-trader stay in lockstep.
function renderTradePolicy(resourceKey) {
  const policy = state.tradePolicies?.[resourceKey] || { mode: 'hold' };
  const sellMode = policy.mode === 'sell_surplus';
  const buyMode  = policy.mode === 'buy_to_reserve';
  return `<div class="cr-policy" data-resource="${escapeHtml(resourceKey)}">
    <div class="cr-policy-label">NPC trade policy</div>
    <div class="cr-policy-row">
      <select class="cr-policy-mode" data-resource="${escapeHtml(resourceKey)}">
        <option value="hold"           ${policy.mode === 'hold' ? 'selected' : ''}>Hold (don't trade)</option>
        <option value="sell_surplus"   ${sellMode ? 'selected' : ''}>Sell surplus above</option>
        <option value="buy_to_reserve" ${buyMode  ? 'selected' : ''}>Buy to reserve of</option>
      </select>
      <label class="cr-policy-field">reserve
        <input class="cr-policy-reserve" type="number" min="0" max="9999"
               value="${policy.reserve_target || 0}" data-resource="${escapeHtml(resourceKey)}"
               ${policy.mode === 'hold' ? 'disabled' : ''} />
      </label>
    </div>
    <div class="cr-policy-row cr-policy-prices">
      <label class="cr-policy-field cr-policy-sell" style="${sellMode ? '' : 'display:none'}">
        sell at $<input class="cr-policy-minsell" type="number" min="1" max="9999" placeholder="any"
               value="${policy.min_sell_price ?? ''}" data-resource="${escapeHtml(resourceKey)}" />+
      </label>
      <label class="cr-policy-field cr-policy-buy" style="${buyMode ? '' : 'display:none'}">
        buy at $<input class="cr-policy-maxbuy" type="number" min="1" max="9999" placeholder="any"
               value="${policy.max_buy_price ?? ''}" data-resource="${escapeHtml(resourceKey)}" />−
      </label>
      <button class="cr-policy-save" data-resource="${escapeHtml(resourceKey)}">Save</button>
    </div>
  </div>`;
}

function renderPartnerTable(resourceKey) {
  if (!cachedFlows) return '';
  const partners = (cachedFlows.byPartner[resourceKey] || []).slice();
  partners.sort((a, b) => (b.export_qty + b.import_qty) - (a.export_qty + a.import_qty));
  if (partners.length === 0) {
    return `<div class="cr-partner-empty">No trade activity for this resource in the last 7 days.</div>`;
  }
  const rows = partners.map((p) => {
    const sent = p.export_qty > 0
      ? `${p.export_qty}${p.export_money ? ' (+$' + Math.round(p.export_money) + ')' : ''}`
      : '—';
    const got = p.import_qty > 0
      ? `${p.import_qty}${p.import_money ? ' (−$' + Math.round(p.import_money) + ')' : ''}`
      : '—';
    return `<div class="cr-partner-row">
      <span class="cr-partner-name">${escapeHtml(p.name)}<small class="cr-partner-kind">${p.kind}</small></span>
      <span class="cr-pos">${escapeHtml(sent)}</span>
      <span class="cr-neg">${escapeHtml(got)}</span>
    </div>`;
  }).join('');
  return `<div class="cr-partners">
    <div class="cr-partners-title">Recent trade activity (7d)</div>
    <div class="cr-partner-row cr-partner-head">
      <span>Partner</span><span>You sent</span><span>You got</span>
    </div>
    ${rows}
  </div>`;
}

function fmtRate(rate) {
  const sign = rate >= 0 ? '+' : '';
  return `${sign}${(Math.round(rate * 10) / 10).toFixed(1)}/min`;
}

